import Delete from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { theme } from '@silbo/ui';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import i18next from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Toaster } from 'sonner';
import './App.css';
import BandwidthIcon, { BandwidthIconPlacement } from './components/BandwidthIcon';
import Loader from './components/Loader';
import NotFound from './components/NotFound';
import VersionNumber from './components/VersionNumber';
import { useApiContext } from './contexts/ApiContext';
import { useAuthenticationContext } from './contexts/AuthenticationContext';
import { useHealthCenterContext } from './contexts/HealthCenterContext';
import { useUserAppContext } from './contexts/UserAppContext';
import AbstractAppError from './errors/AbstractAppError';
import { routeTree } from './routeTree.gen';
import toast from './toast';

const QUERY_STALE_TIME = 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_STALE_TIME,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      let message = i18next.t('errors.anErrorOccurred');

      if (error instanceof AbstractAppError) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message = i18next.t(error.message as any);
      } else if ('code' in error) {
        message = error.code as string;
      } else if (typeof error === 'string') {
        message = error;
      } else if (error.message) {
        message = error.message;
      }

      toast.error(message);
    },
  }),
});

const router = createRouter({
  routeTree,
  context: {
    authenticationContext: undefined!,
    healthCenterContext: undefined!,
    userAppContext: undefined!,
    apiContext: undefined!,
    queryClient,
  },
  defaultPreload: 'intent',
  basepath: import.meta.env.VITE_BASE_URL,
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: () => <Loader />,
  defaultPendingMinMs: 1200,
  defaultNotFoundComponent: () => <NotFound />,
  defaultErrorComponent: () => {
    return <></>;
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const DEV_MODE = !!import.meta.env.DEV;

const getPlacementFromPath = (pathname: string): BandwidthIconPlacement => {
  return pathname.includes('/bed-management') ? 'top-right' : 'bottom-left';
};

const App = () => {
  const healthCenterContext = useHealthCenterContext();
  const authenticationContext = useAuthenticationContext();
  const userAppContext = useUserAppContext();
  const apiContext = useApiContext();
  const [bandwidthIconPlacement, setBandwidthIconPlacement] = useState<BandwidthIconPlacement>(
    getPlacementFromPath(window.location.pathname)
  );
  const previousIsAuthenticatedRef = useRef(authenticationContext.isAuthenticated);

  const onVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then((r) => r?.update());
    }
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [onVisibilityChange]);

  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', ({ toLocation }) => {
      setBandwidthIconPlacement(getPlacementFromPath(toLocation.pathname));
    });

    return () => unsubscribe();
  }, []);

  // Re-run route guards (`beforeLoad`) whenever the authentication state changes so the
  // user is redirected appropriately. This notably covers the Microsoft redirect login,
  // which completes asynchronously in AuthenticationProvider — outside any router context,
  // so it cannot invalidate the router itself. Skips the initial mount to avoid a spurious
  // reload for already-authenticated users.
  useEffect(() => {
    if (previousIsAuthenticatedRef.current !== authenticationContext.isAuthenticated) {
      previousIsAuthenticatedRef.current = authenticationContext.isAuthenticated;
      router.invalidate();
    }
  }, [authenticationContext.isAuthenticated]);

  return (
    <>
      <RouterProvider
        router={router}
        context={{ healthCenterContext, authenticationContext, userAppContext, apiContext }}
      />
      <Toaster />
      {DEV_MODE && (
        <>
          <ReactQueryDevtools />
          <IconButton
            sx={{ position: 'fixed', bottom: 80, right: 16, zIndex: theme.zIndex.appBar + 1 }}
            variant="filled"
            color="primary"
            onClick={() => {
              authenticationContext.logout();
              healthCenterContext.resetHealthCenterConfig();
              userAppContext.resetApp();
              apiContext.removeApiConfig();
              window.location.reload();
            }}
          >
            <Delete />
          </IconButton>
        </>
      )}
      <BandwidthIcon placement={bandwidthIconPlacement} />
      <VersionNumber />
    </>
  );
};

export default App;
